import React, { useState, useEffect } from 'react';
import { PackageCheck, Loader2, Plus, ArrowRightLeft, CheckCircle2, AlertCircle } from 'lucide-react';

import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import SearchableSelectBox from '../components/ui/SearchableSelectBox';
import Modal from '../components/ui/Modal';

import { useToast } from '../hooks/useToast';
import {
    client,
    fetchUtilProductionOrders,
    fetchMaterialRequestHeader,
    fetchMaterialRequestItems,
    fetchInventoryForStoreSales,
    fetchServicesForStoreSale,
    fetchStoresWhereStaffAssigned,
    fetchInventoryItem
} from '../apiHelper';

export const MaterialConsumptionStation = ({ storeId, currentUser }) => {
    const { showToast } = useToast();
    const [loading, setLoading] = useState(false);
    const [submittingAdhoc, setSubmittingAdhoc] = useState(false);

    // Master Dropdown Data
    const [activeOrders, setActiveOrders] = useState([]);
    const [allStores, setAllStores] = useState([]);
    const [componentCatalogOptions, setComponentCatalogOptions] = useState([]);
    const [loadingComponents, setLoadingComponents] = useState(false);

    // Order Selection & Request Context
    const [selectedOrderId, setSelectedOrderId] = useState('');
    const [selectedOrderData, setSelectedOrderData] = useState(null);
    const [requestHeader, setRequestHeader] = useState(null);
    const [requestItems, setRequestItems] = useState([]);

    // Ad-Hoc Form State
    const [adhocData, setAdhocData] = useState({
        targetStoreId: '',
        catentryId: '',
        quantityRequested: '',
        reason: ''
    });

    // --- Partial Issuance Modal State ---
    const [isIssueModalOpen, setIsIssueModalOpen] = useState(false);
    const [selectedLineItem, setSelectedLineItem] = useState(null);
    const [availableLots, setAvailableLots] = useState([]);
    const [loadingLots, setLoadingLots] = useState(false);
    const [lotAllocations, setLotAllocations] = useState({}); // { lot_id: issued_qty }
    const [submittingIssuance, setSubmittingIssuance] = useState(false);

    // 1. Initial Load: Active Orders & Staff Stores
    useEffect(() => {
        if (!storeId) return;
        const loadMasterData = async () => {
            try {
                const [ordersRes, assignedStores] = await Promise.all([
                    fetchUtilProductionOrders({ store_id: storeId }).catch(() => []),
                    fetchStoresWhereStaffAssigned(currentUser).catch(() => [])
                ]);

                const orderList = Array.isArray(ordersRes) ? ordersRes : (ordersRes?.orders || []);
                setActiveOrders(orderList.map(po => ({
                    label: `Order #${po.productionorder_id} - ${po.description || po.product_name || 'Production Run'} (${po.quantity_planned || 1} units)`,
                    value: po.productionorder_id.toString(),
                    rawData: po
                })));

                setAllStores((assignedStores || []).map(s => ({
                    label: s.identifier || `Store ${s.store_id}`,
                    value: s.store_id.toString()
                })));
            } catch (err) {
                showToast("Failed to initialize station master data.", "error");
            }
        };
        loadMasterData();
    }, [storeId, currentUser]);

    // 2. Fetch Catalog Components for Ad-Hoc Requests
    useEffect(() => {
        if (!adhocData.targetStoreId) {
            setComponentCatalogOptions([]);
            return;
        }
        const loadStoreComponents = async () => {
            setLoadingComponents(true);
            try {
                const payload = {
                    ...currentUser,
                    store_id: parseInt(adhocData.targetStoreId, 10),
                    offset: 0,
                    limit: 10000
                };
                const [services, inventory] = await Promise.all([
                    fetchServicesForStoreSale(payload).catch(() => []),
                    fetchInventoryForStoreSales(payload).catch(() => [])
                ]);

                const combined = [...services, ...inventory];
                const itemMap = new Map();
                combined.forEach(lot => {
                    if (lot?.catentry) {
                        itemMap.set(lot.catentry.catentry_id, {
                            label: `${lot.catentry.productname} (${lot.catentry.partnumber || 'No SKU'})`,
                            value: lot.catentry.catentry_id.toString()
                        });
                    }
                });
                setComponentCatalogOptions(Array.from(itemMap.values()));
            } catch (err) {
                showToast("Failed to fetch store catalog.", "error");
            } finally {
                setLoadingComponents(false);
            }
        };
        loadStoreComponents();
    }, [adhocData.targetStoreId, currentUser]);

    // 3. Load Selected Order Requisition
    const handleSelectOrder = async (orderId) => {
        setSelectedOrderId(orderId);
        setRequestHeader(null);
        setRequestItems([]);
        setAdhocData({ targetStoreId: '', catentryId: '', quantityRequested: '', reason: '' });

        if (!orderId) {
            setSelectedOrderData(null);
            return;
        }

        const selectedPoObj = activeOrders.find(o => o.value === orderId)?.rawData;
        setSelectedOrderData(selectedPoObj);

        setLoading(true);
        try {
            const header = await fetchMaterialRequestHeader({ productionorder_id: parseInt(orderId, 10) });
            setRequestHeader(header);

            if (header?.request_id) {
                const items = await fetchMaterialRequestItems({ request_id: header.request_id });
                // console.log(items) // NOTE each item object here now has a source_store_id 
                setRequestItems(Array.isArray(items) ? items : []);
            }
        } catch (err) {
            showToast("Failed to fetch material request for this order.", "error");
        } finally {
            setLoading(false);
        }
    };

    // 4. Submit Ad-Hoc Item Line
    const handleAddAdhocItem = async () => {
        if (!requestHeader?.request_id) {
            showToast("No active material request header found.", "error");
            return;
        }
        if (!adhocData.targetStoreId || !adhocData.catentryId || !adhocData.quantityRequested) {
            showToast("Please select store, material, and quantity.", "error");
            return;
        }

        setSubmittingAdhoc(true);
        try {
            const plannedQty = selectedOrderData?.quantity_planned || 1;
            const parsedQty = parseFloat(adhocData.quantityRequested);

            const payload = {
                request_id: requestHeader.request_id,
                productionorder_id: parseInt(selectedOrderId, 10),
                store_id: parseInt(adhocData.targetStoreId, 10),
                source_store_id: parseInt(adhocData.targetStoreId, 10),
                catentry_id: parseInt(adhocData.catentryId, 10),
                quantity_requested: parsedQty * plannedQty,
                reason: adhocData.reason || "Ad-hoc floor addition"
            };
            console.log(payload)

            await client.postData("/manufacturing/add-adhoc-item", payload);
            showToast("Ad-hoc material added successfully.", "success");

            setAdhocData({ targetStoreId: '', catentryId: '', quantityRequested: '', reason: '' });
            const updatedItems = await fetchMaterialRequestItems({ request_id: requestHeader.request_id });
            setRequestItems(Array.isArray(updatedItems) ? updatedItems : []);
        } catch (err) {
            showToast(err.response?.data?.detail || "Failed to add ad-hoc material.", "error");
        } finally {
            setSubmittingAdhoc(false);
        }
    };

    // 5. Open Partial Issuance Modal & Load Lots
    const handleOpenIssueModal = async (item) => {
        setSelectedLineItem(item);
        setLotAllocations({});
        setIsIssueModalOpen(true);
        setLoadingLots(true);

        try {
            const payload = {
                store_id: item.store_id || parseInt(storeId, 10),
                source_store_id: item.source_store_id,
                catentry_id: item.catentry_id,
                offset: 0,
                limit: 1000
            };

            // Fetch available inventory lots for this specific item & store
            const response = await fetchInventoryItem(payload);
            const filteredLots = (Array.isArray(response) ? response : [])
                .filter(lot => lot.catentry_id === item.catentry_id && lot.current_quantity > 0);

            setAvailableLots(filteredLots);
        } catch (err) {
            showToast("Could not fetch inventory lots for this item.", "error");
            setAvailableLots([]);
        } finally {
            setLoadingLots(false);
        }
    };

    // Handle Quantity Allocation Input Per Lot
    const handleLotQuantityChange = (lotId, qty, maxAvailable) => {
        const parsed = parseFloat(qty) || 0;
        if (parsed > maxAvailable) {
            showToast(`Cannot issue more than available lot quantity (${maxAvailable}).`, "error");
            return;
        }
        setLotAllocations(prev => ({
            ...prev,
            [lotId]: parsed
        }));
    };

    // Auto-fill FIFO allocation helper
    const handleAutoAllocateFIFO = () => {
        if (!selectedLineItem) return;
        const outstandingNeeded = selectedLineItem.quantity_requested - (selectedLineItem.quantity_issued || 0);
        let remainingToAllocate = outstandingNeeded;
        const newAllocations = {};

        for (const lot of availableLots) {
            if (remainingToAllocate <= 0) break;
            const availableInLot = lot.current_quantity || 0;
            const take = Math.min(availableInLot, remainingToAllocate);

            if (take > 0) {
                newAllocations[lot.lot_id] = take;
                remainingToAllocate -= take;
            }
        }

        setLotAllocations(newAllocations);
    };

    // 6. Submit Partial/Full Issuance for Line Item
    const handleConfirmIssuance = async () => {
        if (!selectedLineItem) return;

        const allocationsArray = Object.entries(lotAllocations)
            .map(([lot_id, quantity_issued]) => ({
                inventory_id: parseInt(lot_id, 10),
                quantity_issued: parseFloat(quantity_issued)
            }))
            .filter(a => a.quantity_issued > 0);

        if (allocationsArray.length === 0) {
            showToast("Please enter an issuance quantity for at least one lot.", "error");
            return;
        }

        const totalToIssue = allocationsArray.reduce((acc, curr) => acc + curr.quantity_issued, 0);
        const outstandingNeeded = selectedLineItem.quantity_requested - (selectedLineItem.quantity_issued || 0);

        if (totalToIssue > outstandingNeeded) {
            showToast(`Total allocated (${totalToIssue}) exceeds remaining needed quantity (${outstandingNeeded}).`, "error");
            return;
        }

        setSubmittingIssuance(true);
        try {
            const payload = {
                request_id: requestHeader.request_id,
                item_id: selectedLineItem.item_id,
                store_id: selectedLineItem.store_id || parseInt(storeId, 10),
                source_store_id: selectedLineItem.source_store_id || selectedLineItem.store_id || parseInt(storeId, 10),
                allocations: allocationsArray
            };

            // console.log(payload)

            await client.postData("/manufacturing/issue-partial-material", payload);
            showToast("Material issued successfully!", "success");

            setIsIssueModalOpen(false);
            // Reload requisition items to reflect updated quantity_issued
            const updatedItems = await fetchMaterialRequestItems({ request_id: requestHeader.request_id });
            setRequestItems(Array.isArray(updatedItems) ? updatedItems : []);
        } catch (err) {
            showToast(err.response?.data?.detail || "Failed to process partial stock issuance.", "error");
        } finally {
            setSubmittingIssuance(false);
        }
    };

    return (
        <Card className="p-6 border-zinc-200 shadow-sm bg-white">
            <div className="border-b border-zinc-100 pb-3 mb-6 flex justify-between items-center">
                <div>
                    <h2 className="text-lg font-bold text-zinc-900 flex items-center gap-2">
                        <PackageCheck size={20} className="text-blue-600" /> Material Request & Storekeeper Issuance
                    </h2>
                    <p className="text-xs text-zinc-500">Review BOM requirements and issue available stock per lot to shop floor operations.</p>
                </div>
                {requestHeader && (
                    <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase border ${requestHeader.status === 'ISSUED' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                        requestHeader.status === 'PARTIAL' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                            'bg-amber-50 text-amber-700 border-amber-200'
                        }`}>
                        Status: {requestHeader.status}
                    </span>
                )}
            </div>

            {/* Active Run Dropdown */}
            <div className="max-w-md mb-6">
                <SearchableSelectBox
                    label="Active Production Run"
                    options={activeOrders}
                    value={selectedOrderId}
                    onChange={(e) => handleSelectOrder(e.target.value)}
                    placeholder="Select active order..."
                />
            </div>

            {loading ? (
                <div className="p-8 text-center text-zinc-500">
                    <Loader2 className="animate-spin inline mr-2 text-blue-600" /> Fetching material request parameters...
                </div>
            ) : !selectedOrderId ? (
                <div className="p-8 border border-dashed border-zinc-200 rounded-xl text-center text-zinc-400 text-xs">
                    Select an active production order to inspect and process material requests.
                </div>
            ) : (
                <div className="space-y-6">
                    {/* Material Request Table */}
                    <div className="border rounded-xl overflow-hidden bg-white text-xs">
                        <div className="p-3 bg-zinc-50 border-b border-zinc-200 font-bold text-zinc-700 flex justify-between items-center">
                            <span>Requisition Line Items (Request #{requestHeader?.request_id})</span>
                            <span className="text-[10px] text-zinc-500 font-normal">
                                Planned Batch Size: <strong>{selectedOrderData?.quantity_planned || 1} units</strong>
                            </span>
                        </div>
                        <table className="w-full text-left">
                            <thead className="bg-zinc-100/70 text-zinc-600 border-b border-zinc-200">
                                <tr>
                                    <th className="p-2.5">Component / Part</th>
                                    <th className="p-2.5">Type</th>
                                    <th className="p-2.5">Requested Total</th>
                                    <th className="p-2.5">Issued Total</th>
                                    <th className="p-2.5">Outstanding Balance</th>
                                    <th className="p-2.5 text-right">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-zinc-100">
                                {requestItems.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="p-4 text-center text-zinc-400">No material items linked to this request.</td>
                                    </tr>
                                ) : (
                                    requestItems.map((item) => {
                                        const reqQty = item.quantity_requested || 0;
                                        const issuedQty = item.quantity_issued || 0;
                                        const balance = reqQty - issuedQty;
                                        const isComplete = balance <= 0;

                                        return (
                                            <tr key={item.item_id || item.catentry_id} className="hover:bg-zinc-50/50">
                                                <td className="p-2.5 font-medium text-zinc-900">
                                                    {item.component_name || item.productname || `Item #${item.catentry_id}`}
                                                </td>
                                                <td className="p-2.5">
                                                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${item.is_adhoc ? 'bg-purple-50 text-purple-700 border border-purple-200' : 'bg-zinc-100 text-zinc-600'
                                                        }`}>
                                                        {item.is_adhoc ? 'Ad-Hoc' : 'BOM Planned'}
                                                    </span>
                                                </td>
                                                <td className="p-2.5 font-semibold text-zinc-800">
                                                    {reqQty} {item.unit_measure || 'units'}
                                                </td>
                                                <td className="p-2.5 text-blue-600 font-semibold">
                                                    {issuedQty} {item.unit_measure || 'units'}
                                                </td>
                                                <td className="p-2.5 font-semibold">
                                                    <span className={balance > 0 ? 'text-amber-600' : 'text-emerald-600'}>
                                                        {balance > 0 ? `${balance} ${item.unit_measure || 'units'} remaining` : 'Fully Issued'}
                                                    </span>
                                                </td>
                                                <td className="p-2.5 text-right">
                                                    {isComplete ? (
                                                        <span className="text-emerald-600 font-bold inline-flex items-center gap-1">
                                                            <CheckCircle2 size={14} /> Complete
                                                        </span>
                                                    ) : (
                                                        <Button
                                                            onClick={() => handleOpenIssueModal(item)}
                                                            className="bg-blue-600 hover:bg-blue-700 text-white text-[11px] px-3 py-1 font-medium"
                                                        >
                                                            <ArrowRightLeft size={12} className="mr-1 inline" /> Issue Stock
                                                        </Button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Inline Multi-Store Ad-Hoc Request Builder */}
                    {requestHeader?.status !== 'ISSUED' && (
                        <div className="p-4 bg-zinc-50 border border-zinc-200 rounded-xl space-y-3">
                            <h4 className="text-xs font-bold text-zinc-800 uppercase tracking-wider flex items-center gap-1.5">
                                <Plus size={14} className="text-blue-600" /> Append Ad-Hoc Material Line
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                                <div className="md:col-span-3">
                                    <SearchableSelectBox
                                        label="Source Store"
                                        options={allStores}
                                        value={adhocData.targetStoreId}
                                        onChange={(e) => setAdhocData(p => ({ ...p, targetStoreId: e.target.value, catentryId: '' }))}
                                        placeholder="Select Store..."
                                    />
                                </div>
                                <div className="md:col-span-4">
                                    <SearchableSelectBox
                                        label="Component Item"
                                        options={componentCatalogOptions}
                                        value={adhocData.catentryId}
                                        onChange={(e) => setAdhocData(p => ({ ...p, catentryId: e.target.value }))}
                                        placeholder={loadingComponents ? "Loading catalog..." : "Search material catalog..."}
                                        disabled={!adhocData.targetStoreId || loadingComponents}
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <Input
                                        label="Base Qty (per unit)"
                                        type="number"
                                        value={adhocData.quantityRequested}
                                        onChange={(e) => setAdhocData(p => ({ ...p, quantityRequested: e.target.value }))}
                                        placeholder="e.g. 2"
                                    />
                                </div>
                                <div className="md:col-span-3">
                                    <Input
                                        label="Justification / Reason"
                                        value={adhocData.reason}
                                        onChange={(e) => setAdhocData(p => ({ ...p, reason: e.target.value }))}
                                        placeholder="e.g. Extra scrap replacement"
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end pt-1">
                                <Button
                                    onClick={handleAddAdhocItem}
                                    disabled={submittingAdhoc}
                                    className="bg-zinc-900 hover:bg-zinc-800 text-white text-xs px-4 py-2"
                                >
                                    {submittingAdhoc ? <Loader2 size={14} className="animate-spin mr-1 inline" /> : <Plus size={14} className="mr-1 inline" />} Add to Requisition
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Lot-Level Partial Stock Issuance Modal */}
            <Modal
                isOpen={isIssueModalOpen}
                onClose={() => setIsIssueModalOpen(false)}
                title={`Issue Material: ${selectedLineItem?.component_name || selectedLineItem?.productname || 'Item'}`}
                size="wide"
            >
                {selectedLineItem && (
                    <div className="space-y-4 text-xs">
                        <div className="grid grid-cols-3 gap-3 p-3 bg-zinc-50 border rounded-lg text-zinc-700">
                            <div>
                                <span className="block text-zinc-400 font-bold uppercase text-[10px]">Total Requested</span>
                                <span className="text-sm font-bold text-zinc-900">{selectedLineItem.quantity_requested} {selectedLineItem.unit_measure || 'units'}</span>
                            </div>
                            <div>
                                <span className="block text-zinc-400 font-bold uppercase text-[10px]">Previously Issued</span>
                                <span className="text-sm font-bold text-blue-600">{selectedLineItem.quantity_issued || 0} {selectedLineItem.unit_measure || 'units'}</span>
                            </div>
                            <div>
                                <span className="block text-zinc-400 font-bold uppercase text-[10px]">Outstanding Balance</span>
                                <span className="text-sm font-bold text-amber-600">
                                    {selectedLineItem.quantity_requested - (selectedLineItem.quantity_issued || 0)} {selectedLineItem.unit_measure || 'units'}
                                </span>
                            </div>
                        </div>

                        <div className="flex justify-between items-center">
                            <h4 className="font-bold text-zinc-800">Available Inventory Lots (Store #{selectedLineItem.store_id || storeId})</h4>
                            <Button
                                onClick={handleAutoAllocateFIFO}
                                disabled={loadingLots || availableLots.length === 0}
                                className="bg-zinc-100 hover:bg-zinc-200 text-zinc-800 text-[11px] px-3 py-1 border border-zinc-300"
                            >
                                Auto Fill (FIFO)
                            </Button>
                        </div>

                        {loadingLots ? (
                            <div className="p-6 text-center text-zinc-500">
                                <Loader2 className="animate-spin inline mr-2 text-blue-600" /> Fetching store inventory lots...
                            </div>
                        ) : availableLots.length === 0 ? (
                            <div className="p-4 border border-amber-200 bg-amber-50 rounded-lg text-amber-800 flex items-center gap-2">
                                <AlertCircle size={16} /> No active inventory lots with available stock found for this store.
                            </div>
                        ) : (
                            <div className="border rounded-lg overflow-hidden">
                                <table className="w-full text-left">
                                    <thead className="bg-zinc-100 text-zinc-600 border-b border-zinc-200">
                                        <tr>
                                            <th className="p-2.5">Lot ID / Reference</th>
                                            <th className="p-2.5">On Hand</th>
                                            <th className="p-2.5">Issue Quantity</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-zinc-100">
                                        {availableLots.map(lot => {
                                            const lotId = lot.lot_id;
                                            const qtyOnHand = lot.current_quantity || 0;
                                            return (
                                                <tr key={lotId} className="hover:bg-zinc-50/50">
                                                    <td className="p-2.5 font-mono text-zinc-800">
                                                        Lot #{lotId} {lot.goodsreceipt_id ? `(GR #${lot.goodsreceipt_id})` : ''}
                                                    </td>
                                                    <td className="p-2.5 font-bold text-zinc-700">
                                                        {qtyOnHand}
                                                    </td>
                                                    <td className="p-2.5 w-40">
                                                        <Input
                                                            type="number"
                                                            placeholder="0"
                                                            value={lotAllocations[lotId] || ''}
                                                            onChange={(e) => handleLotQuantityChange(lotId, e.target.value, qtyOnHand)}
                                                        />
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        <div className="flex justify-between items-center pt-3 border-t border-zinc-200">
                            <span className="font-bold text-zinc-800">
                                Total Selected to Issue: {' '}
                                <span className="text-blue-600">
                                    {Object.values(lotAllocations).reduce((acc, curr) => acc + (parseFloat(curr) || 0), 0)}
                                </span>
                            </span>
                            <div className="flex gap-2">
                                <Button
                                    onClick={() => setIsIssueModalOpen(false)}
                                    className="bg-zinc-100 hover:bg-zinc-200 text-zinc-800 text-xs px-4 py-2 border"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleConfirmIssuance}
                                    disabled={submittingIssuance || availableLots.length === 0}
                                    className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-4 py-2 font-bold"
                                >
                                    {submittingIssuance ? <Loader2 size={14} className="animate-spin mr-1 inline" /> : null}
                                    Confirm Issuance
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </Modal>
        </Card>
    );
};